import { PropsWithChildren, FC } from 'react'
import {
  DeflateOptions,
  deflate,
  FeatureGroup,
  DivIcon,
  Point,
  markerClusterGroup,
  MarkerCluster,
  latLngBounds,
  LatLngBounds,
  Layer,
  divIcon,
} from 'leaflet'
import { Feature, Geometry } from 'geojson'
import { createPathComponent } from '@react-leaflet/core'
import { BaseAuctionFragment } from '@foros-fe/graphql'
import { renderToString, renderToStaticMarkup } from 'react-dom/server'
import tw from 'tailwind-styled-components'
import { theme } from '../../../config'
import { formatMoney } from '../../../utils'
import { icon } from '../Marker'

import 'Leaflet.Deflate/src/L.Deflate'
import 'leaflet.markercluster/dist/leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'

type Props = PropsWithChildren<DeflateOptions>
export type AuctionProperties = Pick<BaseAuctionFragment, 'id' | 'highestBidObject' | 'startingBid'>

const Cluster = tw.div`
  w-8
  y-8
  text-center
  font-sans
  font-semibold
  ml-1
  mt-1
  rounded-2xl
  bg-secondary-500
  leading-[32px]
  text-white
  hover:bg-secondary-600
`
const Price = tw.div`
  p-2
  inline-block
  bg-secondary-500
  hover:bg-secondary-600
  active:bg-secondary-700
  rounded-lg
  translate-y-[-11px]
  -translate-x-2/4
  ml-2
`

const AuctionPrice: FC<AuctionProperties> = ({ highestBidObject, startingBid }) => {
  const price = highestBidObject ? highestBidObject.price : startingBid

  return (
    <Price>
      <p className='text-white font-semibold'>{formatMoney(price)}</p>
    </Price>
  )
}

const markerOptions = (({ feature }: Layer & { feature: Feature<Geometry, AuctionProperties> }) => {
  if (feature?.properties.id) {
    return {
      icon: divIcon({
        html: renderToStaticMarkup(<AuctionPrice {...feature.properties} />),
      }),
    }
  }

  return { icon }
}) as DeflateOptions['markerOptions']

export const DeflateLayer = createPathComponent<FeatureGroup, Props>(
  ({ minSize = 30, ...props }, context) => {
    const markerLayer = markerClusterGroup({
      zoomToBoundsOnClick: false,
      maxClusterRadius: 60,
      iconCreateFunction: (cluster) => {
        return new DivIcon({
          html: renderToString(<Cluster>{cluster.getChildCount()}</Cluster>),
          className: 'cluster',
          iconSize: new Point(40, 40),
        })
      },
      polygonOptions: {
        fillColor: theme.colors.secondary[500],
        color: theme.colors.secondary[500],
      },
    })

    markerLayer.on('clusterclick', (event) => {
      const clusterBounds = (event.propagatedFrom as MarkerCluster).getBounds()

      const polygonBounds = layerContainer
        .getLayers()
        .map((layer) => {
          if (!('getBounds' in layer)) return

          const bounds = (layer as FeatureGroup).getBounds()

          return latLngBounds(bounds.getSouthWest(), bounds.getNorthEast())
        })
        .filter((bounds): bounds is LatLngBounds =>
          bounds ? bounds.intersects(clusterBounds) : false
        )
        .reduce((layerBounds, bounds) => layerBounds.extend(bounds))

      context.map.fitBounds(polygonBounds, { padding: [20, 20] })
    })

    const layerContainer = deflate({
      minSize,
      markerLayer,
      markerOptions,
      ...props,
    })

    return {
      instance: layerContainer,
      context: { ...context, layerContainer },
    }
  }
)
